import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Calendar, User, Send, Trash2, ShieldAlert, ShieldCheck } from 'lucide-react';
import L from 'leaflet';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PostDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);

  const isVerified = user?.is_verified || user?.is_admin;

  useEffect(() => {
    fetchPost();
    fetchMessages();
    const pollInterval = setInterval(() => { fetchMessages(); }, 3000);
    return () => { clearInterval(pollInterval); };
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${id}`);
      setPost(response.data);
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages`, {
        post_id: id, receiver_id: post.user_id, message: newMessage
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/posts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Post deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const handleDeleteImage = async (imageIndex, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/posts/${id}/images/${imageIndex}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Image deleted successfully');
      fetchPost();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/messages/${messageId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message deleted successfully');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'rgb(26, 160, 157)' }}></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                className="px-6 py-2.5 rounded-lg shadow-md cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                <h1 className="text-xl font-bold text-white logo-text whitespace-nowrap">STRAY SPOTTER</h1>
              </div>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-btn" className="hover:bg-teal-50">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </div>
            {(user?.id === post.user_id || user?.is_admin) && (
              <Button onClick={handleDelete} variant="outline" data-testid="delete-post-btn" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                {user?.is_admin && user?.id !== post.user_id ? 'Delete Post (Admin)' : 'Delete Post'}
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Images */}
            {post.images && post.images.length > 0 && (
              <Card className="overflow-hidden shadow-lg border-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2" data-testid="post-images">
                  {post.images.map((image, index) => {
                    const isDefaultLogo = (image.includes('placeholder.com') || image.includes('placehold.co')) && image.includes('STRAY+SPOTTER');
                    return (
                      <div key={index} className="relative group">
                        {isDefaultLogo ? (
                          <div className="w-full h-64 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(26, 160, 157)' }}>
                            <h2 className="text-5xl font-bold text-white logo-text">STRAY SPOTTER</h2>
                          </div>
                        ) : (
                          <img src={image} alt={`${post.title} ${index + 1}`} className="w-full h-64 object-cover rounded-lg" />
                        )}
                        {(user?.id === post.user_id || user?.is_admin) && !isDefaultLogo && (
                          <Button onClick={(e) => handleDeleteImage(index, e)} size="sm" className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white shadow-lg" data-testid={`delete-image-${index}`} title="Delete this image">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Details */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{post.title}</CardTitle>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                    <span>By {post.user_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{post.description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Map — Masked for unverified users */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" style={{ color: 'rgb(26, 160, 157)' }} />
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isVerified ? (
                  <>
                    <p className="text-sm text-gray-600 mb-4">{post.location?.address}</p>
                    <div className="h-64 rounded-lg overflow-hidden" data-testid="post-location-map">
                      <MapContainer center={[post.location.lat, post.location.lng]} zoom={13} className="h-full w-full">
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[post.location.lat, post.location.lng]} />
                      </MapContainer>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-6 text-center" data-testid="location-masked">
                    <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                    <h3 className="font-bold text-lg text-amber-800 mb-2">Location Hidden</h3>
                    <p className="text-amber-700 text-sm mb-4">
                      To protect the animal's safety, the exact location is only visible to verified users. Please verify your identity by uploading a valid ID in your profile.
                    </p>
                    <Button
                      onClick={() => navigate('/profile')}
                      data-testid="verify-identity-btn"
                      className="text-white"
                      style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Verify My Identity
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Chat Sidebar */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-0 sticky top-24">
              <CardHeader>
                <CardTitle>Chat with Spotter</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col h-[500px]">
                  <div className="flex-1 overflow-y-auto mb-4 space-y-3 p-2" data-testid="chat-messages">
                    {messages.length === 0 ? (
                      <p className="text-center text-gray-500 text-sm py-8">No messages yet. Start the conversation!</p>
                    ) : (
                      messages.map((msg, idx) => (
                        <div key={msg.id || idx} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                          <div className="relative group">
                            <div
                              className={`max-w-[80%] rounded-2xl px-4 py-2 ${msg.sender_id === user?.id ? 'text-white' : 'bg-white text-gray-900 border border-gray-200'}`}
                              style={msg.sender_id === user?.id ? { backgroundColor: 'rgb(26, 160, 157)' } : {}}
                            >
                              <p className="text-xs font-medium mb-1 opacity-80">{msg.sender_name}</p>
                              <p className="text-sm">{msg.message}</p>
                            </div>
                            {(user?.is_admin || msg.sender_id === user?.id) && (
                              <button onClick={() => handleDeleteMessage(msg.id)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600" title="Delete message">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type your message..." data-testid="message-input" className="flex-1" />
                    <Button type="submit" data-testid="send-message-btn" style={{ backgroundColor: 'rgb(26, 160, 157)' }} className="hover:opacity-90 text-white">
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
